import z from "zod";
import { DayOfWeek, GenderComposition } from "@/openapi/generated";
import { preprocessRequiredDecimal } from "@/lib/utils/form";

const numberPreprocess = (val: unknown): number => {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const parsed = parseFloat(val);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const intPreprocess = (val: unknown): number => {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const parsed = parseInt(val, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const timeValueSchema = z.object({
  hour: z.string().min(1, { message: "Hour is required" }),
  minute: z.string().min(1, { message: "Minute is required" }),
  period: z.string().min(1, { message: "Period is required" }),
});

export const classDaySchema = z.object({
  day: z.nativeEnum(DayOfWeek, { message: "Day is required" }),
  startTime: timeValueSchema,
  endTime: timeValueSchema,
});

const dateValueSchema = z.object({
  day: z.string(),
  month: z.string(),
  year: z.string(),
});

export const classEditFormSchema = z
  .object({
    classId: z.preprocess(
      intPreprocess,
      z.number().min(1, { message: "Class ID is required" }),
    ),
    name: z.string().min(1, { message: "Name is required" }),
    classStartDate: dateValueSchema.refine(
      (val) => val.day && val.month && val.year,
      { message: "Start date is required" }
    ),
    classEndDate: dateValueSchema.nullable().optional(),
    isActive: z.boolean(),
    minAge: z.preprocess(
      preprocessRequiredDecimal,
      z
        .union([z.number(), z.undefined()])
        .refine((val) => val !== undefined, {
          message: "Min age is required",
        })
        .pipe(z.number().min(0, { message: "Min age must be 0 or greater" })),
    ),
    maxAge: z.preprocess(
      preprocessRequiredDecimal,
      z
        .union([z.number(), z.undefined()])
        .refine((val) => val !== undefined, {
          message: "Max age is required",
        })
        .pipe(z.number().min(0, { message: "Max age must be 0 or greater" })),
    ),
    locationId: z.preprocess(
      intPreprocess,
      z.number().min(1, { message: "Location is required" }),
    ),
    genderComposition: z.nativeEnum(GenderComposition, {
      message: "Gender composition is required",
    }),
    isContinuous: z.boolean(),
    requiresApproval: z.boolean().nullable(),
    hasWaitList: z.boolean().nullable(),
    classStartTime: z.string().nullable().optional(),
    classEndTime: z.string().nullable().optional(),
    allowOnlineRegistration: z.boolean().nullable().optional(),
    billSeparately: z.boolean().nullable().optional(),
    maxNumberOfStudents: z.preprocess(
      (val) =>
        val === "" || val === null || val === undefined
          ? undefined
          : typeof val === "string"
            ? parseInt(val, 10)
            : val,
      z
        .union([
          z.number().min(1, { message: "Max number of students must be at least 1" }),
          z.undefined(),
        ])
        .refine((v) => v !== undefined, { message: "Max number of students is required" }),
    ),
    hideOnLiveSchedule: z.boolean().nullable().optional(),
    isFeatured: z.boolean().optional(),
    recital: z.boolean().nullable().optional(),
    offersMakeup: z.boolean().optional(),
    tuition: z.preprocess(
      (val) =>
        val === "" || val === null || val === undefined
          ? null
          : numberPreprocess(val),
      z
        .number()
        .min(0, { message: "Tuition must be 0 or greater" })
        .nullable()
        .optional(),
    ),
    tuitionRatePlanId: z.string().optional().nullable(),
    registrationFee: z.preprocess(
      (val) =>
        val === "" || val === null || val === undefined
          ? null
          : numberPreprocess(val),
      z
        .number()
        .min(0, { message: "Registration fee must be 0 or greater" })
        .nullable()
        .optional(),
    ),
    applySeasonRegistrationFee: z.boolean().nullable().optional(),
    prorateTuition: z.boolean().nullable().optional(),
    description: z.string().nullable().optional(),
    classRoomId: z.preprocess(
      (val) =>
        val === "" || val === null || val === undefined
          ? null
          : typeof val === "string"
            ? parseInt(val, 10)
            : val,
      z
        .number()
        .min(1, { message: "Classroom ID must be 1 or greater" })
        .nullable()
        .optional(),
    ),
    seasonId: z.preprocess(
      (val) =>
        val === "" || val === null || val === undefined
          ? null
          : typeof val === "string"
            ? parseInt(val, 10)
            : val,
      z
        .number()
        .min(1, { message: "Season ID must be 1 or greater" })
        .nullable()
        .optional(),
    ),
    teacherIds: z.preprocess(
      (val) => {
        if (!Array.isArray(val)) return [];
        const ids: number[] = [];
        for (const item of val) {
          const id = typeof item === "string" ? parseInt(item, 10) : Number(item);
          if (Number.isFinite(id) && id > 0 && !ids.includes(id)) ids.push(id);
        }
        return ids;
      },
      z.array(z.number()).min(1, { message: "Select at least one teacher" }),
    ),
    classDays: z.array(classDaySchema).nullable().optional(),
  })
  .refine(
    (data) => {
      if (
        data.minAge !== null &&
        data.minAge !== undefined &&
        data.maxAge !== null &&
        data.maxAge !== undefined &&
        data.minAge > data.maxAge
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Max age must be greater than or equal to min age",
      path: ["maxAge"],
    },
  )
  .refine(
    (data) => {
      if (!data.isContinuous) {
        if (!data.classEndDate) return false;
        if (!data.classEndDate.day || !data.classEndDate.month || !data.classEndDate.year) {
          return false;
        }
      }
      return true;
    },
    {
      message: "End date is required",
      path: ["classEndDate"],
    },
  )
  .refine(
    (data) => {
      if (data.classStartDate && data.classEndDate && data.classStartDate.year && data.classStartDate.month && data.classStartDate.day && data.classEndDate.year && data.classEndDate.month && data.classEndDate.day) {
        const startDateStr = `${data.classStartDate.year}-${data.classStartDate.month.padStart(2, "0")}-${data.classStartDate.day.padStart(2, "0")}`;
        const endDateStr = `${data.classEndDate.year}-${data.classEndDate.month.padStart(2, "0")}-${data.classEndDate.day.padStart(2, "0")}`;
        // Compare strings directly (YYYY-MM-DD format is lexicographically sortable)
        return endDateStr >= startDateStr;
      }
      return true;
    },
    {
      message: "End date must be after or equal to start date",
      path: ["classEndDate"],
    },
  );

export type ClassEditFormSchemaType = z.infer<typeof classEditFormSchema>;
